import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProductFilters, type SortOption } from '../components/ProductFilters';
import { ProductGrid } from '../components/ProductGrid';
import { categoryName } from '../data/catalog';
import { useCommerce } from '../contexts/CommerceContext';
import type { CollectionSlug, Product } from '../types';

type CatalogMode = 'catalog' | 'novidades' | 'mais-vendidos';

function isCollection(value: string | undefined): value is CollectionSlug {
  return value === 'fitness' || value === 'moda-praia' || value === 'lingerie';
}

export function CatalogPage({ mode = 'catalog', forcedCollection }: { mode?: CatalogMode; forcedCollection?: CollectionSlug }) {
  const { collection: routeCollection, subcategory } = useParams();
  const { products, isCatalogLoading } = useCommerce();
  const initialCollection = forcedCollection ?? (isCollection(routeCollection) ? routeCollection : 'todos');
  const [collection, setCollection] = useState<'todos' | CollectionSlug>(initialCollection);
  const [category, setCategory] = useState(subcategory ?? 'todos');
  const [sort, setSort] = useState<SortOption>(mode === 'mais-vendidos' ? 'best-sellers' : 'recent');
  const [size, setSize] = useState('todos');
  const [color, setColor] = useState('todos');
  const [availability, setAvailability] = useState('todos');

  useEffect(() => {
    setCollection(forcedCollection ?? (isCollection(routeCollection) ? routeCollection : 'todos'));
    setCategory(subcategory ?? 'todos');
  }, [forcedCollection, routeCollection, subcategory]);

  const colors = useMemo(() => [...new Set(products.flatMap((product) => product.colors.map((item) => item.name)))], [products]);
  const filteredProducts = useMemo(() => {
    let result: Product[] = [...products];
    if (mode === 'novidades') result = result.filter((product) => product.newProduct);
    if (mode === 'mais-vendidos') result = result.filter((product) => product.bestSeller);
    if (collection !== 'todos') result = result.filter((product) => product.collection === collection);
    if (collection === 'fitness' && category !== 'todos') result = result.filter((product) => product.tags.includes(category));
    if (size !== 'todos') result = result.filter((product) => product.sizes.includes(size));
    if (color !== 'todos') result = result.filter((product) => product.colors.some((item) => item.name === color));
    if (availability !== 'todos') result = result.filter((product) => {
      const soldOut = product.stockByVariant && Object.values(product.stockByVariant).every((stock) => stock < 1);
      return availability === 'esgotado' ? soldOut : !soldOut;
    });
    return result.sort((a, b) => {
      if (sort === 'best-sellers') return Number(Boolean(b.bestSeller)) - Number(Boolean(a.bestSeller));
      if (sort === 'low-price') return (a.promotionalPrice ?? a.price ?? Number.POSITIVE_INFINITY) - (b.promotionalPrice ?? b.price ?? Number.POSITIVE_INFINITY);
      if (sort === 'high-price') return (b.promotionalPrice ?? b.price ?? Number.NEGATIVE_INFINITY) - (a.promotionalPrice ?? a.price ?? Number.NEGATIVE_INFINITY);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [products, collection, category, size, color, availability, sort, mode]);

  const title = mode === 'novidades' ? 'Novidades para acompanhar seu ritmo.' : mode === 'mais-vendidos' ? 'As escolhas que mais encantam.' : collection === 'fitness' && category !== 'todos' ? categoryName(category) : 'Encontre a peça ideal para você.';
  const description = mode === 'catalog' ? 'Explore nossa curadoria de fitness, moda praia e lingerie.' : 'Seleções especiais da Sena Beachway.';

  return <main className="catalog-page page-shell">
    <header className="page-intro"><p className="eyebrow">{mode === 'catalog' ? 'Catálogo' : mode === 'novidades' ? 'Novidades' : 'Mais vendidos'}</p><h1>{title}</h1><p>{description}</p></header>
    {mode === 'catalog' && <ProductFilters collection={collection} subcategory={category} sort={sort} size={size} color={color} availability={availability} colors={colors} onCollection={(value) => { setCollection(value); if (value !== 'fitness') setCategory('todos'); }} onSubcategory={setCategory} onSort={setSort} onSize={setSize} onColor={setColor} onAvailability={setAvailability} />}
    <div className="catalog-result-meta"><span>{filteredProducts.length} {filteredProducts.length === 1 ? 'peça encontrada' : 'peças encontradas'}</span>{isCatalogLoading && <span>Atualizando catálogo…</span>}</div>
    <ProductGrid products={filteredProducts} emptyMessage={collection === 'moda-praia' || collection === 'lingerie' ? 'Esta coleção será lançada em breve. Volte para acompanhar as novidades.' : 'Nenhuma peça encontrada com estes filtros.'} />
  </main>;
}
